export interface SegmentRule {
  field: string;
  op: string;
  value: string;
}

export type SegmentMatch = "all" | "any";

export interface SegmentDetail {
  id: string;
  name: string;
  description: string;
  member_count: number;
  last_pushed_at: string | null;
  updated_at?: string;
  status: "synced" | "draft";
  rules?: SegmentRule[];
  match?: SegmentMatch;
  tags?: string[];
  used_in_campaigns?: number;
}

export interface IssueArticle {
  title: string;
  url: string;
  category?: string;
}

export type ContentBlockType =
  | "heading"
  | "text"
  | "image"
  | "link"
  | "article"
  | "divider"
  | "hero"
  | "tip"
  | "cta"
  | "footer";

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  text?: string;
  url?: string;
  alt?: string;
}

export interface IssueUtm {
  source: string;
  medium: string;
  campaign: string;
  content?: string;
}

export interface EditorComment {
  id: string;
  block_id?: string;
  text: string;
  author: string;
  at: string;
  resolved: boolean;
}

export interface IssueVersion {
  id: string;
  n: number;
  status: IssueDetail["status"];
  at: string;
  actor: string;
  title: string;
  subject: string;
  blocks: ContentBlock[];
  /** The newsletter's structure. A version without it restores a hybrid. */
  sections?: NewsletterSection[];
  categories?: string[];
  notes?: string;
  preheader?: string;
  utm?: IssueUtm;
}

/**
 * A newsletter is a category-driven template, not a fixed email.
 *
 * The editor builds one structure; what a recipient actually receives is
 * resolved per subscriber from the categories they subscribe to. A section
 * with `category: null` follows the newsletter's audience — that is the
 * personalisation. A section pinned to an explicit category shows the same
 * content to everyone, which is occasionally what you want (a company
 * announcement) and is the exception.
 */
export type SectionType =
  | "category"   // the category name that anchors the issue
  | "featured"   // one primary story, large treatment
  | "articles"   // related articles, list or grid
  | "block"      // picture, heading, description and link, arranged by layout
  | "banner"     // full-width promotional band
  | "tip"        // health tip
  | "doctor"     // doctor or content recommendation
  | "text"
  | "image"
  | "cta"
  | "divider"
  | "spacer"
  | "footer";    // logo, social links, unsubscribe

/**
 * How a content block arranges its picture, heading, description and link.
 *
 * "start" and "end" rather than left and right: these newsletters are
 * Arabic-first, so the picture sits on the side the reader starts from —
 * the right in an RTL issue, the left in an LTR one.
 */
export type BlockLayout =
  | "stacked" | "image-below" | "image-start" | "image-end" | "text" | "overlay";

export const BLOCK_LAYOUTS: { value: BlockLayout; label: string; hint: string }[] = [
  { value: "stacked", label: "Picture above", hint: "Picture, then heading, description, link" },
  { value: "image-below", label: "Picture below", hint: "Words first, picture underneath them" },
  { value: "image-start", label: "Picture before", hint: "Beside the words, on the side the reader starts from" },
  { value: "image-end", label: "Picture after", hint: "Beside the words, on the far side" },
  { value: "text", label: "No picture", hint: "Heading, description and link only" },
  { value: "overlay", label: "Text over picture", hint: "A full-width band with the text on top" },
];

/**
 * One link in a newsletter: the address, what the editor wants said about it,
 * and a picture placed wherever it reads best.
 *
 * The description is the editor's, not the CMS's — the picker fills the title
 * and the picture and deliberately leaves this alone.
 */
export interface SectionArticle {
  id: string;
  title: string;
  url: string;
  /** The description shown under the link. Editable. */
  excerpt?: string;
  image?: string;
  /** Where the picture sits around the link and its description. */
  layout?: ArticleLayout;
  category?: string;
  read_minutes?: number;
}

/** Same vocabulary as a content block, minus the overlay a link cannot use. */
export type ArticleLayout = Exclude<BlockLayout, "overlay">;

export const ARTICLE_LAYOUTS: { value: ArticleLayout; label: string }[] =
  BLOCK_LAYOUTS.filter((l) => l.value !== "overlay").map((l) => ({
    value: l.value as ArticleLayout,
    label: l.label,
  }));

export interface NewsletterSection {
  id: string;
  type: SectionType;
  /** Section heading, e.g. "مقالات ذات صلة". */
  heading?: string;
  /** null or absent = follow the recipient's own category. */
  category?: string | null;
  /** "list"/"grid" for article sections; a BlockLayout for a content block. */
  layout?: "list" | "grid" | BlockLayout;
  /** How many articles to resolve per recipient when this section is dynamic. */
  count?: number;
  articles?: SectionArticle[];
  text?: string;
  url?: string;
  image?: string;
  cta_label?: string;
}

export interface IssueDetail {
  id: string;
  title: string;
  subject: string;
  status: "draft" | "in_review" | "approved" | "sent" | "scheduled";
  updated_at: string;
  author: string;
  articles: number;
  open_rate: number | null;
  articles_list?: IssueArticle[];
  blocks?: ContentBlock[];
  notes?: string;
  preheader?: string;
  sender_name?: string;
  reply_to?: string;
  utm?: IssueUtm;
  reviewer?: string | null;
  next_action?: string;
  comments?: EditorComment[];
  versions?: IssueVersion[];
  /** Audience categories this newsletter targets. Drives article selection. */
  categories?: string[];
  /** The newsletter structure. Supersedes `blocks`. */
  sections?: NewsletterSection[];
}

export type IssueEditable = Partial<
  Pick<IssueDetail,
    | "title" | "subject" | "notes" | "blocks" | "preheader" | "sender_name"
    | "reply_to" | "utm" | "categories" | "sections">
>;
